import React, { useCallback } from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import { annotationIdToLabelMapAtom, annotationMetadataAtom, currentFrameLoadedAnnotationsAtom } from '../../../../../labelling-v2/atoms/annotation-atoms';
import { dispatchAtom } from '../../../../../labelling-v2/atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../../../../../labelling-v2/atoms/label-store-dispatch/events';
import { mlTrackingJobsAtom } from '../../../../../labelling-v2/atoms/ml-tracking-atoms';
import { STUDIO_WRITE_ACTIONS } from '../../../../../labelling-v2/atoms/studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../../../../labelling-v2/atoms/studio-store-write/dispatch-studio-store-write';
import type { FramePayload } from '../../../../../labelling-v2/studio-store/types';
import { reconstructAnnotationFromFramePayload } from '../../../../../labelling-v2/utils/reconstruct-annotation-from-frame-payload';
import type { AnnotationType, BoundingBoxT } from '../../../../types/annotations.type';
import { ACTION_TYPE } from '../../constants';
import { SequenceActions } from './annotation-timeline-config-sub-components/sequence-actions';
import { useKeyframeStepper } from './hooks/use-keyframe-stepper';
import { useSequenceValidation } from './hooks/use-sequence-validation';
import { AnnotationSequence } from './annotation-sequence';
import type { AnnotationTimelineConfigPropsT } from './annotation-timeline-config';
import { KeyframeStepperDisplay } from './keyframe-stepper-display';
type PropsT = Omit<AnnotationTimelineConfigPropsT, 'onPropagationMethodChange'> & {
  totalFrames: number;
};
const payloadToBox = (payload: FramePayload | undefined): BoundingBoxT | null => {
  const geometry = payload?.geometry;
  if (!geometry || !('width' in geometry)) {
    return null;
  }
  const {
    x,
    y,
    width,
    height
  } = (geometry as BoundingBoxT);
  return {
    x,
    y,
    width,
    height
  };
};
export const KeyframeStepperSequence = ({
  annotationId,
  annotationLabel,
  maxFrames,
  currentFrame,
  actionConfig = [ACTION_TYPE.TRACK],
  timeDisplayMode,
  frameRate,
  totalFrames,
  onClose
}: PropsT) => {
  const [css, theme] = useStyletron();
  const dispatch = useSetAtom(dispatchAtom);
  const dispatchStudioStoreWrite = useSetAtom(dispatchStudioStoreWriteAtom);
  const setMlTrackingJobs = useSetAtom(mlTrackingJobsAtom);
  const currentFrameAnnotations = useAtomValue(currentFrameLoadedAnnotationsAtom);
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationIdToLabelMap = useAtomValue(annotationIdToLabelMapAtom);
  const annotationType: AnnotationType = annotationMetadata.get(annotationId)?.annotationType ?? 'RECTANGLE';
  const displayName = `${annotationLabel.title} [${annotationId}]`;
  const dispatchSetCurrentFrame = useCallback((frame: number) => {
    dispatch({
      type: EVENTS.SET_CURRENT_FRAME,
      frameId: frame
    });
  }, [dispatch]);
  const {
    chips,
    isLastKeyframe,
    canTrack,
    goToNext,
    buildJob,
    endSession,
    startFrame,
    endFrame,
    setEndFrame
  } = useKeyframeStepper({
    currentFrame,
    maxFrames,
    annotationId,
    annotationType,
    displayName,
    dispatchSetCurrentFrame
  });

  // Route the range inputs through the persisted session so Start stays pinned to the first keyframe
  // and End edits survive the popover remounting on each Next.
  const setNewRange = useCallback<React.Dispatch<React.SetStateAction<{
    start: number;
    end: number;
  }>>>(update => {
    const next = typeof update === 'function' ? update({
      start: startFrame,
      end: endFrame
    }) : update;
    setEndFrame(next.end);
  }, [startFrame, endFrame, setEndFrame]);
  const {
    isSequenceValid,
    isTrackingValid,
    validationError
  } = useSequenceValidation({
    newRange: {
      start: startFrame,
      end: endFrame
    },
    maxFrames,
    isTrackingMode: false
  });
  const handleNext = useCallback(() => {
    const currentPayload = currentFrameAnnotations.get(annotationId);
    const currentBox = payloadToBox(currentPayload);
    if (isLastKeyframe) {
      const job = buildJob(currentBox);
      if (job) {
        setMlTrackingJobs(jobs => [...jobs, job]);
      }
      endSession();
      onClose?.();
      return;
    }
    const arrivalFrame = goToNext(currentBox);

    // Carry the current box forward and pin it as a MANUAL keyframe at the frame we
    // just stepped to, so every step is a visible, adjustable anchor. A single
    // WRITE_ANNOTATION makes the keyframe render on the timeline: it adds both a span
    // entry and a keyframe entry at the arrival frame
    const metadata = annotationMetadata.get(annotationId);
    if (arrivalFrame !== null && currentPayload && metadata) {
      dispatchStudioStoreWrite({
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex: arrivalFrame,
        annotationId,
        annotation: reconstructAnnotationFromFramePayload({
          ...currentPayload,
          isKeyFrame: true,
          keyFrameType: 'MANUAL'
        }, metadata, annotationIdToLabelMap)
      });
    }
  }, [currentFrameAnnotations, annotationId, isLastKeyframe, buildJob, setMlTrackingJobs, onClose, goToNext, endSession, annotationMetadata, annotationIdToLabelMap, dispatchStudioStoreWrite]);
  return <div className={css({
    backgroundColor: theme.colors.backgroundPrimary,
    fontFamily: 'Uber Move'
  })} data-testid="keyframe-stepper-sequence" data-action-config={JSON.stringify(actionConfig)}>
      <AnnotationSequence setNewRange={setNewRange} currentFrame={currentFrame} startFrame={startFrame} initialEndFrame={endFrame} maxFrames={maxFrames} timeDisplayMode={timeDisplayMode} frameRate={frameRate} totalFrames={totalFrames} />

      <KeyframeStepperDisplay chips={chips} />

      <SequenceActions actionConfig={actionConfig} isTracking={false} isInterpolating={false} isSequenceValid={isSequenceValid && canTrack} isTrackingValid={isTrackingValid} validationError={validationError} showNextButton onNext={handleNext} />
    </div>;
};